import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Select from './Select'
import {
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  CheckCircle2,
  X,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react'
import {
  exportClientsToCSV,
  exportClientsToExcel,
  importClients,
  parseImportFile,
  detectColumnMappings,
  getAvailableFields,
} from '../services/clientImportExport'

const ClientImportExport = ({ onImportComplete }) => {
  const [activeTab, setActiveTab] = useState('export') // 'export' or 'import'
  const [importFile, setImportFile] = useState(null)
  const [importPreview, setImportPreview] = useState(null)
  const [columnMapping, setColumnMapping] = useState({})
  const [importing, setImporting] = useState(false)
  const [importResults, setImportResults] = useState(null)
  const [exportFormat, setExportFormat] = useState('excel') // 'excel' or 'csv'
  const [exporting, setExporting] = useState(false)
  const fileInputRef = useRef(null)
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  const availableFields = getAvailableFields()

  const handleFileSelect = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setImportFile(file)
    setImportResults(null)

    try {
      // Parse file to preview
      const data = await parseImportFile(file)
      setImportPreview(data.slice(0, 5)) // Preview first 5 rows

      // Auto-detect column mappings
      const headers = Object.keys(data[0] || {})
      const autoMappings = detectColumnMappings(headers)
      setColumnMapping(autoMappings)
    } catch (error) {
      alert(error.message || 'Error parsing file')
      setImportFile(null)
      setImportPreview(null)
    }
  }

  const handleImport = async () => {
    if (!importFile || !organizationId || !canWrite('clients')) return

    // Validate required mappings
    const requiredFields = availableFields.filter((f) => f.required)
    const missingFields = requiredFields.filter((f) => !columnMapping[f.value])
    if (missingFields.length > 0) {
      alert(`Please map the following required columns: ${missingFields.map((f) => f.label).join(', ')}`)
      return
    }

    try {
      setImporting(true)
      const results = await importClients(organizationId, importFile, columnMapping)
      setImportResults(results)

      if (results.imported > 0 && onImportComplete) {
        onImportComplete()
      }
    } catch (error) {
      alert(error.message || 'Failed to import clients')
    } finally {
      setImporting(false)
    }
  }

  const handleExport = async () => {
    if (!organizationId) return

    try {
      setExporting(true)
      if (exportFormat === 'excel') {
        await exportClientsToExcel(organizationId)
      } else {
        await exportClientsToCSV(organizationId)
      }
      alert('Export completed successfully!')
    } catch (error) {
      alert(error.message || 'Failed to export clients')
    } finally {
      setExporting(false)
    }
  }

  const resetImport = () => {
    setImportFile(null)
    setImportPreview(null)
    setColumnMapping({})
    setImportResults(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Card variant="glass" className="p-6">
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('export')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
            activeTab === 'export'
              ? 'border-b-2 border-primary-600 text-primary-700'
              : 'text-gray-600 hover:text-primary-600'
          }`}
        >
          <Download size={18} />
          Export
        </button>
        <button
          onClick={() => setActiveTab('import')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
            activeTab === 'import'
              ? 'border-b-2 border-primary-600 text-primary-700'
              : 'text-gray-600 hover:text-primary-600'
          }`}
        >
          <Upload size={18} />
          Import
        </button>
      </div>

      {activeTab === 'export' && (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Export Clients</h3>
            <p className="text-sm text-gray-600 mb-4">
              Export all clients to Excel or CSV format for backup or external use.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Select
              label="Export Format"
              options={[
                { value: 'excel', label: 'Excel (.xlsx)' },
                { value: 'csv', label: 'CSV (.csv)' },
              ]}
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="w-48"
            />
            <div className="flex-1" />
            <Button onClick={handleExport} disabled={exporting || !canWrite('clients')}>
              {exporting ? (
                <>
                  <FileSpreadsheet size={18} className="mr-2 animate-spin" />
                  Exporting...
                </>
              ) : exportFormat === 'excel' ? (
                <>
                  <FileSpreadsheet size={18} className="mr-2" />
                  Export to Excel
                </>
              ) : (
                <>
                  <FileText size={18} className="mr-2" />
                  Export to CSV
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'import' && (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Clients</h3>
            <p className="text-sm text-gray-600 mb-4">
              Import clients from CSV or Excel files. Map your column headers to client fields.
            </p>
          </div>

          {!importFile ? (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
              <Upload size={48} className="mx-auto mb-4 text-gray-400" />
              <p className="text-sm text-gray-600 mb-4">
                Select a CSV or Excel file to import
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canWrite('clients')}
              >
                <Upload size={18} className="mr-2" />
                Choose File
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Info */}
              <Card variant="soft" className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {importFile.name.endsWith('.xlsx') || importFile.name.endsWith('.xls') ? (
                      <FileSpreadsheet size={24} className="text-green-600" />
                    ) : (
                      <FileText size={24} className="text-blue-600" />
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{importFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(importFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={resetImport}
                    className="text-gray-400 hover:text-gray-600"
                    title="Remove file"
                  >
                    <X size={20} />
                  </button>
                </div>
              </Card>

              {/* Column Mapping */}
              {importPreview && importPreview.length > 0 && (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Map Columns</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Match your file columns to client fields. Required fields are marked with *.
                    </p>
                  </div>

                  <div className="space-y-3">
                    {availableFields.map((field) => {
                      const fileHeaders = Object.keys(importPreview[0] || {})
                      return (
                        <div key={field.value} className="flex items-center gap-3">
                          <div className="w-40">
                            <label className="block text-sm font-semibold text-gray-700">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                          </div>
                          <ArrowRight size={16} className="text-gray-400" />
                          <Select
                            value={columnMapping[field.value] || ''}
                            onChange={(e) =>
                              setColumnMapping({ ...columnMapping, [field.value]: e.target.value })
                            }
                            options={[
                              { value: '', label: '-- Select Column --' },
                              ...fileHeaders.map((header) => ({
                                value: header,
                                label: header,
                              })),
                            ]}
                            className="flex-1"
                            required={field.required}
                          />
                        </div>
                      )
                    })}
                  </div>

                  {/* Preview */}
                  <Card variant="soft" className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Preview (First 5 Rows)</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            {Object.keys(importPreview[0] || {}).map((header) => (
                              <th key={header} className="text-left p-2 font-semibold text-gray-700">
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.map((row, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              {Object.values(row).map((value, colIdx) => (
                                <td key={colIdx} className="p-2 text-gray-600">
                                  {String(value || '').slice(0, 30)}
                                  {String(value || '').length > 30 ? '...' : ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>

                  {/* Import Results */}
                  {importResults && (
                    <Card
                      variant="soft"
                      className={`p-4 ${
                        importResults.imported > 0
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {importResults.imported > 0 ? (
                          <CheckCircle2 size={24} className="text-green-600 flex-shrink-0" />
                        ) : (
                          <AlertCircle size={24} className="text-red-600 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-2">Import Results</h4>
                          <div className="space-y-1 text-sm">
                            <p>
                              <strong>Total rows:</strong> {importResults.total}
                            </p>
                            <p>
                              <strong>Valid rows:</strong> {importResults.valid}
                            </p>
                            <p>
                              <strong>Imported:</strong> {importResults.imported}
                            </p>
                            {importResults.invalid > 0 && (
                              <p className="text-red-600">
                                <strong>Invalid rows:</strong> {importResults.invalid}
                              </p>
                            )}
                            {importResults.errors.length > 0 && (
                              <div className="mt-2">
                                <p className="font-semibold text-red-600">Errors:</p>
                                {importResults.errors.map((error, idx) => (
                                  <p key={idx} className="text-xs text-red-600">
                                    Batch {error.batch}: {error.error}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Import Button */}
                  <div className="flex gap-3">
                    <Button
                      onClick={handleImport}
                      disabled={importing || !canWrite('clients')}
                      className="flex-1"
                    >
                      {importing ? (
                        <>
                          <Upload size={18} className="mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload size={18} className="mr-2" />
                          Import Clients
                        </>
                      )}
                    </Button>
                    <Button variant="secondary" onClick={resetImport} className="flex-1">
                      <X size={18} className="mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

export default ClientImportExport

